"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { bookmarksApi, BookmarkCategory } from "@/lib/api/public-client";
import { usePublicAuth } from "@/lib/auth/PublicAuthContext";
interface BookmarkItem {
  slug: string;
  category: BookmarkCategory;
  title?: string;
  thumbnail_url?: string;
  description?: string;
  article_url?: string;
}
interface BookmarkContextType {
  // Check if a slug is bookmarked (O(1) lookup)
  isBookmarked: (slug: string) => boolean;
  // Toggle bookmark status
  toggleBookmark: (item: BookmarkItem) => Promise<boolean>;
  // Loading state for initial fetch
  isLoading: boolean;
  // Refresh bookmarks from server
  refreshBookmarks: () => Promise<void>;
}
const BookmarkContext = createContext<BookmarkContextType | null>(null);
export function BookmarkProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    isAuthenticated
  } = usePublicAuth();
  const [bookmarkedSlugs, setBookmarkedSlugs] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);

  // Fetch all bookmarks once when user is authenticated
  const fetchBookmarks = useCallback(async () => {
    if (!isAuthenticated) {
      setBookmarkedSlugs(new Set());
      return;
    }
    setIsLoading(true);
    try {
      const response = await bookmarksApi.getBookmarks();
      const slugs = new Set(response.bookmarks.map((b: any) => b.slug));
      setBookmarkedSlugs(slugs);
    } catch (error) {
      console.error("Failed to fetch bookmarks:", error);
      setBookmarkedSlugs(new Set());
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch bookmarks when authentication state changes
  useEffect(() => {
    fetchBookmarks();
  }, [fetchBookmarks]);

  // O(1) lookup for bookmark status
  const isBookmarkedFn = useCallback((slug: string): boolean => {
    return bookmarkedSlugs.has(slug);
  }, [bookmarkedSlugs]);

  // Toggle bookmark with optimistic update
  const toggleBookmark = useCallback(async (item: BookmarkItem): Promise<boolean> => {
    if (!isAuthenticated) {
      return false;
    }
    const wasBookmarked = bookmarkedSlugs.has(item.slug);

    // Optimistic update
    setBookmarkedSlugs(prev => {
      const next = new Set(prev);
      if (wasBookmarked) {
        next.delete(item.slug);
      } else {
        next.add(item.slug);
      }
      return next;
    });
    try {
      const result = await bookmarksApi.toggleBookmark({
        slug: item.slug,
        category: item.category,
        title: item.title,
        thumbnail_url: item.thumbnail_url,
        description: item.description,
        article_url: item.article_url
      });

      // Sync with server response
      setBookmarkedSlugs(prev_1 => {
        const next_1 = new Set(prev_1);
        if (result.bookmarked) {
          next_1.add(item.slug);
        } else {
          next_1.delete(item.slug);
        }
        return next_1;
      });
      return result.bookmarked;
    } catch (error_0) {
      // Revert optimistic update on error
      setBookmarkedSlugs(prev_0 => {
        const next_0 = new Set(prev_0);
        if (wasBookmarked) {
          next_0.add(item.slug);
        } else {
          next_0.delete(item.slug);
        }
        return next_0;
      });
      console.error("Failed to toggle bookmark:", error_0);
      throw error_0;
    }
  }, [isAuthenticated, bookmarkedSlugs]);
  const value = useMemo(() => ({
    isBookmarked: isBookmarkedFn,
    toggleBookmark,
    isLoading,
    refreshBookmarks: fetchBookmarks
  }), [isBookmarkedFn, toggleBookmark, isLoading, fetchBookmarks]);
  return <BookmarkContext.Provider value={value}>
      {children}
    </BookmarkContext.Provider>;
}
export function useBookmarks() {
  const context = useContext(BookmarkContext);
  if (!context) {
    throw new Error("useBookmarks must be used within a BookmarkProvider");
  }
  return context;
}

// Safe hook that doesn't throw if used outside provider
export function useBookmarksSafe() {
  return useContext(BookmarkContext);
}